import type {
    ChatMessage,
    MastraAgentRef,
    SproutStreamModelId,
    SproutStreamReasoningEffort,
} from '@fruitful/llm-service-shared';
import React, { useEffect, useRef } from 'react';
import { YStack } from 'smoothie';

import { useAiChatRenderAnalytics } from '../hooks/useAiChatRenderAnalytics';
import { useMastraChat } from '../hooks/useMastraChat';
import { ChatContainer } from './ChatContainer';
import { ThreadList } from './shared-chat/ThreadList';

interface MastraChatProps {
    /** The Mastra agent to connect to */
    agent: MastraAgentRef;
    /** Optional thread ID for conversation persistence */
    threadId?: string;
    /** Optional resource ID for user identification */
    resourceId?: string;
    /** Optional initial messages to populate the chat */
    initialMessages?: ChatMessage[];
    /** Storage key used to persist the Mastra thread ID across sessions */
    persistThreadStorageKey?: string;
    /** When true, loads prior messages from the persisted thread on mount */
    restoreThreadMessages?: boolean;
    /** Whether the chat input should be disabled */
    disabled?: boolean;
    /** Placeholder text for the input */
    placeholder?: string;
    /** Message to show when there are no messages */
    emptyStateMessage?: string;
    /** Greeting rendered on new chat splash screen */
    greeting?: string;
    /** Suggested prompts to show when chat is empty */
    suggestions?: string[];
    /** Intro paragraphs on the empty-state splash. */
    introCopy?: readonly string[];
    /** Footer disclosure under the chat input. */
    aiDisclosure?: string;
    /** When true, requests the agent's opening message on mount instead of showing a static greeting */
    autoGreet?: boolean;
    /** When set with resourceId, shows the thread list sheet */
    isThreadListOpen?: boolean;
    /** Called when the thread list sheet should close */
    onThreadListOpenChange?: (isOpen: boolean) => void;
    /** Called when the active Mastra thread ID changes */
    onThreadIdChange?: (threadId: string) => void;
    /** Allowlisted Sprout model for the member stream */
    sproutStreamModelId?: SproutStreamModelId;
    /** Allowlisted OpenAI reasoning effort for the member stream */
    sproutStreamReasoningEffort?: SproutStreamReasoningEffort;
    /** Signed-in membership id, injected server-side into holistic bills */
    membershipId?: string;
    /** Callback when a new message is sent */
    onMessageSent?: (message: ChatMessage) => void;
    /** Callback when a response is received */
    onResponseReceived?: (message: ChatMessage) => void;
}

export const MastraChat = ({
    agent,
    threadId,
    resourceId,
    initialMessages,
    persistThreadStorageKey,
    restoreThreadMessages = false,
    disabled = false,
    placeholder,
    emptyStateMessage,
    greeting,
    suggestions,
    introCopy,
    aiDisclosure,
    autoGreet = false,
    isThreadListOpen = false,
    onThreadListOpenChange,
    onThreadIdChange,
    sproutStreamModelId,
    sproutStreamReasoningEffort,
    membershipId,
    onMessageSent,
    onResponseReceived,
}: MastraChatProps) => {
    const {
        messages,
        threadId: activeThreadId,
        isStreaming,
        showTypingIndicator,
        isRestoringHistory,
        sendMessage,
        sendMessageWithAgentPayload,
        sendSilentMessage,
        requestGreeting,
        clearMessages,
        switchToThread,
        stopStreaming,
    } = useMastraChat({
        agent,
        threadId,
        resourceId,
        persistThreadStorageKey,
        restoreThreadMessages,
        sproutStreamModelId,
        sproutStreamReasoningEffort,
        membershipId,
        onMessageSent,
        onResponseReceived,
    });

    const { trackAgentResponsePartRendered } = useAiChatRenderAnalytics(
        messages,
        isRestoringHistory,
    );

    const hasSentInitialMessage = useRef(false);

    useEffect(() => {
        onThreadIdChange?.(activeThreadId);
    }, [activeThreadId, onThreadIdChange]);

    // Auto-greet: request the agent's opening message on mount
    useEffect(() => {
        if (autoGreet && !hasSentInitialMessage.current) {
            hasSentInitialMessage.current = true;
            setTimeout(() => {
                void requestGreeting();
            }, 100);
        }
    }, [autoGreet, requestGreeting]);

    // Automatically send user messages from initialMessages
    useEffect(() => {
        if (
            !hasSentInitialMessage.current &&
            initialMessages != null &&
            initialMessages.length > 0
        ) {
            const userMessages = initialMessages.filter(
                (msg) => msg.role === 'user',
            );
            if (userMessages.length > 0) {
                const lastUserMessage = userMessages[userMessages.length - 1];
                if (lastUserMessage != null) {
                    const textContent = lastUserMessage.content.find(
                        (c) => c.type === 'text',
                    );
                    if (textContent?.type === 'text') {
                        hasSentInitialMessage.current = true;
                        setTimeout(() => {
                            void sendMessage(textContent.text);
                        }, 100);
                    }
                }
            }
        }
    }, [initialMessages, sendMessage]);

    const showThreadList = onThreadListOpenChange != null && resourceId != null;

    return (
        <YStack flex={1} minHeight={0}>
            <ChatContainer
                messages={messages}
                isStreaming={isStreaming}
                showTypingIndicator={showTypingIndicator}
                isRestoringHistory={isRestoringHistory}
                onSendMessage={sendMessage}
                onSendMessageWithAgentPayload={sendMessageWithAgentPayload}
                onSendSilentMessage={sendSilentMessage}
                onStopStreaming={stopStreaming}
                disabled={disabled || isRestoringHistory}
                placeholder={placeholder}
                emptyStateMessage={emptyStateMessage}
                greeting={autoGreet ? undefined : greeting}
                suggestions={autoGreet ? [] : suggestions}
                introCopy={introCopy}
                aiDisclosure={aiDisclosure}
                sproutStreamModelId={sproutStreamModelId}
                sproutStreamReasoningEffort={sproutStreamReasoningEffort}
                agentKey={agent.agentKey}
                onContentPartRendered={trackAgentResponsePartRendered}
            />
            {showThreadList ? (
                <ThreadList
                    isOpen={isThreadListOpen}
                    onClose={() => {
                        onThreadListOpenChange(false);
                    }}
                    agentId={agent.agentKey}
                    resourceId={resourceId}
                    onSelectThread={(nextThreadId) => {
                        void switchToThread(nextThreadId);
                    }}
                    onNewChat={clearMessages}
                />
            ) : null}
        </YStack>
    );
};
